import { useEffect, useState } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Typography, Alert
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../services/api';

const newspaperSchema = z.object({
  title: z.string().min(1, 'Укажите название'),
  issueNumber: z.string().optional(),
  year: z.number().min(1900).max(new Date().getFullYear() + 1),
  // Пути загружаются кнопкой и хранятся относительными (assets/pdf/...),
  // поэтому z.string().url() здесь отвергал собственные данные. Формат проверяет бэкенд.
  pdfUrl: z.string().optional(),
  coverUrl: z.string().optional(),
});

type NewspaperFormValues = z.infer<typeof newspaperSchema>;

interface NewspapersFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NewspaperFormValues) => Promise<void>;
  initialData?: NewspaperFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function NewspapersForm({ open, onClose, onSubmit, initialData, loading, error }: NewspapersFormProps) {
  const [pdfUploading, setPdfUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<NewspaperFormValues>({
    resolver: zodResolver(newspaperSchema),
    defaultValues: {
      title: '',
      issueNumber: '',
      year: new Date().getFullYear(),
      pdfUrl: '',
      coverUrl: '',
    },
  });

  const pdfUrl = watch('pdfUrl');
  const coverUrl = watch('coverUrl');

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        title: '',
        issueNumber: '',
        year: new Date().getFullYear(),
        pdfUrl: '',
        coverUrl: '',
      });
    }
  }, [initialData, open, reset]);

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await api.post('/upload/document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setValue('pdfUrl', response.data.data.url);
    } catch (err) {
      console.error('PDF upload error:', err);
    } finally {
      setPdfUploading(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setValue('coverUrl', response.data.data.url);
    } catch (err) {
      console.error('Cover upload error:', err);
    } finally {
      setCoverUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать журнал/газету' : 'Добавить журнал/газету'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название *"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Номер выпуска"
                {...register('issueNumber')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Год *"
                type="number"
                error={!!errors.year}
                helperText={errors.year?.message}
                {...register('year', { valueAsNumber: true })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                PDF файл
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  disabled={pdfUploading}
                >
                  {pdfUploading ? 'Загрузка...' : 'Загрузить PDF'}
                  <input type="file" hidden accept=".pdf" onChange={handlePdfUpload} />
                </Button>
                {pdfUrl && (
                  <Typography variant="body2" color="text.secondary">
                    ✓ Загружен
                  </Typography>
                )}
              </Box>
              <TextField
                fullWidth
                label="или укажите URL"
                error={!!errors.pdfUrl}
                helperText={errors.pdfUrl?.message}
                {...register('pdfUrl')}
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Обложка
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                  disabled={coverUploading}
                >
                  {coverUploading ? 'Загрузка...' : 'Загрузить обложку'}
                  <input type="file" hidden accept="image/*" onChange={handleCoverUpload} />
                </Button>
                {coverUrl && (
                  <Typography variant="body2" color="text.secondary">
                    ✓ Загружена
                  </Typography>
                )}
              </Box>
              <TextField
                fullWidth
                label="или укажите URL"
                error={!!errors.coverUrl}
                helperText={errors.coverUrl?.message}
                {...register('coverUrl')}
                sx={{ mt: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={loading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}